import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { APP_ROUTES } from '@/common/constants'
import { BundleT } from '@/common/types'
import { BUNDLE_COPY } from '@/locales/en'
import { ArrowRight, LineChart, Shield } from 'lucide-react'
import { useNavigate } from 'react-router'

export const BundleCardLoadingState: React.FC = () => {
    return (
        <div className="w-full self-stretch p-6 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-border-outline inline-flex flex-col justify-between items-start overflow-hidden min-h-0 h-full animate-pulse">
            <div className="flex-1 w-full inline-flex flex-col justify-start items-start gap-4 min-h-0">
                <div className="self-stretch inline-flex justify-between items-center w-full">
                    <div className="p-2 bg-stone-100 rounded-lg w-8 h-8" />
                    <div className="w-56 inline-flex flex-col justify-center items-end">
                        <div className="h-4 w-20 bg-stone-100 rounded" />
                        <div className="h-3 w-14 bg-stone-100 rounded mt-2" />
                    </div>
                </div>

                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="h-4 w-40 bg-stone-100 rounded" />
                    <div className="h-4 w-44 bg-stone-100 rounded" />
                </div>

                <div className="flex flex-col justify-start items-start gap-2 flex-1 min-h-0">
                    <div className="h-4 w-24 bg-stone-100 rounded" />
                    <div className="inline-flex justify-start items-start gap-2">
                        <div className="h-6 w-14 bg-stone-100 rounded-2xl" />
                        <div className="h-6 w-16 bg-stone-100 rounded-2xl" />
                        <div className="h-6 w-12 bg-stone-100 rounded-2xl" />
                    </div>
                </div>

                <div className="h-4 w-28 bg-stone-100 rounded mt-auto" />
            </div>
        </div>
    )
}
export const BundleCard: React.FC<{ bundle?: BundleT }> = ({ bundle }) => {
    const navigate = useNavigate()
    if (!bundle) {
        return <BundleCardLoadingState />
    }

    const viewBundle = () => {
        analytics.trackWithDefaults(ANALYTICS_EVENTS.TRADING.BUNDLE_SELECTED, { bundle: bundle.slug })
        navigate(`${APP_ROUTES.BUNDLES}/${bundle.slug}`)
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={viewBundle}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    viewBundle()
                }
            }}
            className="w-full self-stretch p-6 bg-white rounded-2xl outline outline-1 outline-offset-[-1px] outline-border-outline inline-flex flex-col justify-between items-start overflow-hidden min-h-0 h-full cursor-pointer"
        >
            <div className="flex-1 w-full inline-flex flex-col justify-start items-start gap-4 min-h-0">
                <div className="self-stretch inline-flex justify-between items-center">
                    <div className="p-2 bg-primary-dust rounded-lg flex justify-start items-center gap-2.5">
                        {bundle.type === 'GROWTH' ? (
                            <LineChart className="w-6 h-6 text-content-primary" />
                        ) : (
                            <Shield className="w-6 h-6 text-content-primary" />
                        )}
                    </div>
                </div>
                <div className="self-stretch flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch justify-center text-content-primary text-base font-medium font-['Inter'] leading-5">
                        {bundle.name}
                    </div>
                    <div className="self-stretch justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                        {bundle.shortDescription}
                    </div>
                </div>
                <div className="flex flex-col justify-start items-start gap-2 flex-1 min-h-0">
                    <div className="justify-center text-black text-sm font-normal font-['Inter'] leading-5">
                        {BUNDLE_COPY.etfsIncluded}:
                    </div>
                    <div className="inline-flex justify-start items-start gap-2">
                        {bundle.securities.map((security: any) => (
                            <div
                                key={security.securityCode}
                                data-size="small"
                                data-state="Default"
                                data-type="Grey"
                                className="px-3 py-1 bg-primary-dust rounded-2xl backdrop-blur-[5px] flex justify-start items-center gap-1 overflow-hidden"
                            >
                                <div className="justify-center text-content-secondary text-xs font-normal font-['Inter'] leading-4">
                                    {security.securityCode}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="text-black text-sm font-medium font-['Inter'] underline leading-6 px-0 flex items-center hover:no-underline">
                    {BUNDLE_COPY.viewBundle}
                    <ArrowRight className="ml-2 w-4 h-4 text-black" />
                </div>
            </div>
        </div>
    )
}
